import { createContext, useCallback, useEffect, useState } from "react";
import api from "../api";
import { userCan } from "../constants/modules";

export const AuthContext = createContext();

function AuthProvider({ children }) {
  // Hydrate from localStorage so new tabs / refreshes stay signed in
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => localStorage.getItem("isLoggedIn") === "true"
  );

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const raw = localStorage.getItem("currentUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [authToken, setAuthToken] = useState(
    () => localStorage.getItem("authToken") || ""
  );

  // Persist the logged-in flag — and clear everything else on logout
  useEffect(() => {
    if (isLoggedIn) {
      localStorage.setItem("isLoggedIn", "true");
    } else {
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("authToken");
      setCurrentUser(null);
      setAuthToken("");
    }
  }, [isLoggedIn]);

  // Persist the current user
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem("currentUser", JSON.stringify(currentUser));
    } else {
      localStorage.removeItem("currentUser");
    }
  }, [currentUser]);

  // Persist the auth token
  useEffect(() => {
    if (authToken) {
      localStorage.setItem("authToken", authToken);
    } else {
      localStorage.removeItem("authToken");
    }
  }, [authToken]);

  // Keep this device's FCM push token fresh in the database. The login page
  // handles the permission prompt + first token on a fresh sign-in; here we
  // only refresh SILENTLY on a restored session (page refresh) if the user has
  // already granted permission. Lazy-loaded + best-effort so it never blocks.
  useEffect(() => {
    if (!isLoggedIn || !authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { autoRegisterPush } = await import("../utils/push");
        if (!cancelled) await autoRegisterPush(false);
      } catch {
        // ignore — push is optional and must never block the app
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, authToken]);

  // On a restored session (page refresh / new tab) re-fetch the current user
  // from the backend so their role + permissions reflect any changes an admin
  // made since login — without forcing a re-login. Best-effort; a 401 is handled
  // by the auth:expired flow below.
  useEffect(() => {
    if (!isLoggedIn || !authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (!cancelled && res?.user) setCurrentUser(res.user);
      } catch {
        // ignore — stale local copy is fine until the next real request
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once per session restore (token change), not on every currentUser edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, authToken]);

  // The API wrapper fires "auth:expired" on any 401 response — log out here
  useEffect(() => {
    function handleExpired() {
      setIsLoggedIn(false);
    }
    window.addEventListener("auth:expired", handleExpired);
    return () => window.removeEventListener("auth:expired", handleExpired);
  }, []);

  // Role / permission helpers consumed across the app to gate UI. Admins
  // always pass; a non-admin is governed entirely by their permission map.
  const isAdmin = currentUser?.role === "admin";
  const hasPermission = useCallback(
    (moduleKey, action) => userCan(currentUser, moduleKey, action),
    [currentUser]
  );

  // Used by Login.jsx after a successful POST /api/auth/login
  function signIn({ token, user }) {
    setAuthToken(token);
    setCurrentUser(user);
    setIsLoggedIn(true);
  }

  // Used by Header.jsx logout — call the backend best-effort, then clear local state
  async function signOut() {
    // Stop any in-flight domain scan so it doesn't keep running in the
    // background after the user leaves. Best-effort + no-op when nothing runs.
    try {
      await api.post("/domain-monitors/scan/cancel");
    } catch {
      // Ignore — cancelling is best-effort
    }
    // Unsubscribe this device's FCM push token so the logged-out device stops
    // receiving notifications. disablePush() removes it from the DB
    // (/push/unsubscribe), deletes the Firebase token, and clears localStorage.
    // Must run BEFORE we clear authToken below, since /push/unsubscribe is
    // behind verifyJwt. Lazy-loaded + best-effort so it never blocks logout.
    try {
      const { disablePush } = await import("../utils/push");
      await disablePush();
    } catch {
      // Ignore — push cleanup is best-effort and must never block logout
    }
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore network/server errors — we still clear locally
    }
    setIsLoggedIn(false);
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        currentUser,
        setCurrentUser,
        authToken,
        isAdmin,
        hasPermission,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
